/**
 * DEV-ONLY: Layout inspector. Activates via ?inspect=… when ENABLE_DEV_INSPECTOR is true.
 * Persists state via DevtoolsSession (route-scoped localStorage).
 * Modes: classic (default) vs smart — see resolveInspectorMode.js and DEVTOOLS.md.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import ClassicInspectorPanel from "./ClassicInspectorPanel";
import SmartInspectorPanel from "./SmartInspectorPanel";
import DevtoolsPanelShell from "./DevtoolsPanelShell";
import { useDevtoolsStack } from "./DevtoolsStackContext";
import { useDevtoolsSession } from "./DevtoolsSessionContext";
import { getElementIntrospection } from "./layoutInspectorIntrospection";
import { INSPECTOR_MODE_SMART, isInspectorUrlActivated, resolveInspectorMode } from "./resolveInspectorMode";
import {
  applyMediaCompositionOverrides,
  clearMediaCompositionOverrides,
  getMediaCompositionTargetMeta,
  getReviewCardForAnyElement,
  isImageCompositionTarget,
  mergeMediaCompositionState,
  applyReviewCardHeightOverride,
  clearReviewCardHeightOverride,
  readMediaCompositionFromDom,
  readReviewCardHeightFromDom,
} from "./layoutInspectorMedia";
import { COLOR_STYLE_APPLY, getColorLayoutFields } from "./layoutInspectorColorFields";
import "./LayoutInspector.css";

export const ENABLE_DEV_INSPECTOR = process.env.NODE_ENV !== "production";

const MIN_SIZE = 4;
const INSPECTOR_PANEL_SELECTOR = ".layout-inspector-panel";
const DESIGN_OVERLAY_PANEL_SELECTOR = ".design-overlay__panel";
const DEVTOOLS_STACK_SELECTOR = ".devtools-stack";

function parsePx(value) {
  if (!value || typeof value !== "string") return 0;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function getBoxSides(style, prefix) {
  return {
    top: parsePx(style[`${prefix}Top`]),
    right: parsePx(style[`${prefix}Right`]),
    bottom: parsePx(style[`${prefix}Bottom`]),
    left: parsePx(style[`${prefix}Left`]),
  };
}

function getBoxModelRects(el) {
  if (!el || !el.getBoundingClientRect) return null;
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  const border = getBoxSides(style, "borderWidth");
  const padding = getBoxSides(style, "padding");
  const margin = getBoxSides(style, "margin");

  return {
    margin: {
      left: rect.left - margin.left,
      top: rect.top - margin.top,
      width: rect.width + margin.left + margin.right,
      height: rect.height + margin.top + margin.bottom,
    },
    border: {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    },
    padding: {
      left: rect.left + border.left,
      top: rect.top + border.top,
      width: rect.width - border.left - border.right,
      height: rect.height - border.top - border.bottom,
    },
    content: {
      left: rect.left + border.left + padding.left,
      top: rect.top + border.top + padding.top,
      width: rect.width - border.left - border.right - padding.left - padding.right,
      height: rect.height - border.top - border.bottom - padding.top - padding.bottom,
    },
  };
}

/** Editable style props: key used in overrides state, style property name, unit suffix for display. */
const EDITABLE_PROPS = [
  { key: "padding", styleProp: "padding", unit: "px" },
  { key: "margin", styleProp: "margin", unit: "px" },
  { key: "width", styleProp: "width", unit: "px" },
  { key: "maxWidth", styleProp: "maxWidth", unit: "px" },
  { key: "fontSize", styleProp: "fontSize", unit: "px" },
  { key: "lineHeight", styleProp: "lineHeight", unit: "" },
  { key: "gap", styleProp: "gap", unit: "px" },
  { key: "borderRadius", styleProp: "borderRadius", unit: "px" },
];

function getEditableComputed(el) {
  if (!el || !el.getBoundingClientRect) return null;
  const style = window.getComputedStyle(el);
  const rect = el.getBoundingClientRect();
  const padding = getBoxSides(style, "padding");
  const margin = getBoxSides(style, "margin");
  const lineHeight = style.lineHeight;
  const lineHeightNum = lineHeight === "normal" ? "" : parseFloat(lineHeight);
  return {
    padding: Math.round(padding.top),
    margin: Math.round(margin.top),
    width: Math.round(rect.width),
    maxWidth: parsePx(style.maxWidth) || "",
    fontSize: Math.round(parsePx(style.fontSize)),
    lineHeight: Number.isFinite(lineHeightNum) ? lineHeightNum : "",
    gap: Math.round(parsePx(style.gap)) || "",
    borderRadius: Math.round(parsePx(style.borderRadius)) || "",
  };
}

function applyOverridesToElement(el, overrides) {
  if (!el || !el.style) return;
  EDITABLE_PROPS.forEach(({ key, styleProp, unit }) => {
    const val = overrides[key];
    el.style[styleProp] = val !== undefined && val !== "" ? `${val}${unit}` : "";
  });
  COLOR_STYLE_APPLY.forEach(({ key, styleProp }) => {
    const val = overrides[key];
    el.style[styleProp] = val !== undefined && val !== "" ? String(val).trim() : "";
  });
  if (isImageCompositionTarget(el) && overrides.mediaComposition && typeof overrides.mediaComposition === "object") {
    applyMediaCompositionOverrides(el, overrides.mediaComposition);
  }
  if (overrides.reviewCardHeightPx !== undefined && overrides.reviewCardHeightPx !== "") {
    applyReviewCardHeightOverride(el, overrides.reviewCardHeightPx);
  }
}

function clearOverridesFromElement(el, overrides = {}) {
  if (!el || !el.style) return;
  EDITABLE_PROPS.forEach(({ styleProp }) => {
    el.style[styleProp] = "";
  });
  COLOR_STYLE_APPLY.forEach(({ styleProp }) => {
    el.style[styleProp] = "";
  });
  if (isImageCompositionTarget(el)) {
    clearMediaCompositionOverrides(el, overrides.mediaComposition);
  }
  if (getReviewCardForAnyElement(el)) {
    clearReviewCardHeightOverride(el);
  }
}

function getLayoutInfo(el) {
  if (!el || !el.getBoundingClientRect) return null;
  const style = window.getComputedStyle(el);
  const rect = el.getBoundingClientRect();
  const border = getBoxSides(style, "borderWidth");
  const padding = getBoxSides(style, "padding");
  const margin = getBoxSides(style, "margin");

  return {
    tagName: el.tagName?.toLowerCase() ?? "",
    className: el.className && typeof el.className === "string" ? el.className : "",
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    margin: `${margin.top}px ${margin.right}px ${margin.bottom}px ${margin.left}px`,
    padding: `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`,
    borderRadius: style.borderRadius || "0",
    fontSize: style.fontSize || "",
    lineHeight: style.lineHeight || "",
    display: style.display || "",
    position: style.position || "",
    gap: style.gap || "",
    flexDirection: style.flexDirection || "",
    justifyContent: style.justifyContent || "",
    alignItems: style.alignItems || "",
    editableComputed: getEditableComputed(el),
    colorFields: getColorLayoutFields(el),
  };
}

function isInspectable(el) {
  if (!el || el === document.body || el === document.documentElement) return false;
  if (el.getAttribute?.("data-devtools-ignore") === "true") return false;
  if (el.closest?.(INSPECTOR_PANEL_SELECTOR)) return false;
  if (el.closest?.(DESIGN_OVERLAY_PANEL_SELECTOR)) return false;
  if (el.closest?.(DEVTOOLS_STACK_SELECTOR)) return false;
  const tag = (el.tagName || "").toLowerCase();
  if (["script", "style", "head", "meta", "link", "path", "svg"].includes(tag)) return false;
  const rect = el.getBoundingClientRect?.();
  if (!rect || rect.width < MIN_SIZE || rect.height < MIN_SIZE) return false;
  return true;
}

function cssEscape(val) {
  if (typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(val);
  }
  return String(val).replace(/"/g, '\\"');
}

function deriveStableElementKey(el) {
  if (!el || !el.getAttribute) return null;
  const did = el.getAttribute("data-devtools-id");
  if (did) return { type: "data-devtools-id", value: did };
  const id = el.getAttribute("id");
  if (id) return { type: "id", value: id };
  return null;
}

function resolveStableElementKey(key) {
  if (!key) return null;
  const type = key.type || key.strategy;
  const value = key.value;
  if (!type || !value) return null;
  try {
    if (type === "data-devtools-id") {
      return document.querySelector(`[data-devtools-id="${cssEscape(String(value))}"]`);
    }
    if (type === "id") {
      return document.getElementById(String(value));
    }
    return null;
  } catch {
    return null;
  }
}

export default function LayoutInspectorGlobal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const stack = useDevtoolsStack();
  const sessionApi = useDevtoolsSession();
  const sessionInspector = sessionApi?.session?.inspector;

  const inspectorActivatedByUrl = isInspectorUrlActivated(searchParams);
  const inspectActive = inspectorActivatedByUrl || Boolean(sessionInspector?.enabled);
  const inspectorMode = resolveInspectorMode(searchParams);

  const [hoveredElement, setHoveredElement] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedBoxModel, setSelectedBoxModel] = useState(null);
  const [layoutInfo, setLayoutInfo] = useState(null);
  const [showHoverOverlay, setShowHoverOverlay] = useState(true);
  const [overrides, setOverrides] = useState({});
  const overridesRef = useRef({});
  const rafRef = useRef(null);
  const previousSelectedRef = useRef(null);
  const restoredSelectionRef = useRef(false);

  useEffect(() => {
    overridesRef.current = overrides;
  }, [overrides]);

  const mediaCompositionInfo = useMemo(() => {
    if (!selectedElement || !isImageCompositionTarget(selectedElement)) return null;
    const meta = getMediaCompositionTargetMeta(selectedElement);
    const fromDom = readMediaCompositionFromDom(selectedElement, meta);
    const merged = mergeMediaCompositionState(overrides.mediaComposition, fromDom, meta);
    return { meta, merged, fromDom };
  }, [selectedElement, overrides.mediaComposition]);

  const reviewCardHeightInfo = useMemo(() => {
    if (!selectedElement) return null;
    const card = getReviewCardForAnyElement(selectedElement);
    if (!card) return null;
    const fromDom = readReviewCardHeightFromDom(card);
    const mergedPx =
      overrides.reviewCardHeightPx !== undefined && overrides.reviewCardHeightPx !== ""
        ? Number(overrides.reviewCardHeightPx)
        : fromDom.heightPx;
    return {
      card,
      meta: { label: "CSS variable on .mcp2-review-card", varName: "--review-card-height" },
      fromDom,
      merged: { heightPx: Number.isFinite(mergedPx) ? mergedPx : fromDom.heightPx },
    };
  }, [selectedElement, overrides.reviewCardHeightPx]);

  const smartIntrospection = useMemo(() => {
    if (!selectedElement || inspectorMode !== INSPECTOR_MODE_SMART) return null;
    return getElementIntrospection(selectedElement);
  }, [selectedElement, inspectorMode]);

  const handleMediaCompositionPatch = useCallback((patch) => {
    setOverrides((prev) => {
      if (!selectedElement || !isImageCompositionTarget(selectedElement)) return prev;
      const meta = getMediaCompositionTargetMeta(selectedElement);
      const fromDom = readMediaCompositionFromDom(selectedElement, meta);
      const base = mergeMediaCompositionState(prev.mediaComposition, fromDom, meta);
      return {
        ...prev,
        mediaComposition: { ...base, ...patch },
      };
    });
  }, [selectedElement]);

  const handleReviewCardHeightPatch = useCallback((patch) => {
    setOverrides((prev) => {
      if (!selectedElement) return prev;
      const card = getReviewCardForAnyElement(selectedElement);
      if (!card) return prev;
      const fromDom = readReviewCardHeightFromDom(card);
      const nextPx = patch?.heightPx;
      const n = Number(nextPx);
      return {
        ...prev,
        reviewCardHeightPx: Number.isFinite(n) ? n : fromDom.heightPx,
      };
    });
  }, [selectedElement]);

  // New route = new session scope; allow restore attempt again.
  useEffect(() => {
    restoredSelectionRef.current = false;
  }, [location.pathname]);

  const updateHoverRect = useCallback((el) => {
    if (!el || !isInspectable(el)) {
      setHoveredElement(null);
      setHoverRect(null);
      return;
    }
    setHoveredElement(el);
    setHoverRect(el.getBoundingClientRect());
  }, []);

  const updateSelection = useCallback((el, nextOverrides = null, selectionKey = null) => {
    if (!el) {
      if (previousSelectedRef.current) {
        clearOverridesFromElement(previousSelectedRef.current, overridesRef.current);
        previousSelectedRef.current = null;
      }
      setSelectedElement(null);
      setSelectedBoxModel(null);
      setLayoutInfo(null);
      setOverrides({});
      sessionApi?.setSession((prev) => ({
        inspector: { ...(prev.inspector || {}), enabled: true, selectedElement: null, tempEdits: {} },
      }));
      return;
    }
    if (previousSelectedRef.current && previousSelectedRef.current !== el) {
      clearOverridesFromElement(previousSelectedRef.current, overridesRef.current);
    }
    previousSelectedRef.current = el;
    setSelectedElement(el);
    setSelectedBoxModel(getBoxModelRects(el));
    setLayoutInfo(getLayoutInfo(el));
    setOverrides(nextOverrides || {});

    const stableKey = selectionKey || deriveStableElementKey(el);
    sessionApi?.setSession((prev) => ({
      inspector: {
        ...(prev.inspector || {}),
        enabled: true,
        selectedElement: stableKey,
        tempEdits: nextOverrides || {},
      },
    }));
  }, [sessionApi]);

  useEffect(() => {
    if (!selectedElement?.isConnected) return;
    applyOverridesToElement(selectedElement, overrides);
  }, [selectedElement, overrides]);

  // Seed inspector enabled into session when user activates via URL.
  useEffect(() => {
    if (!sessionApi) return;
    if (!ENABLE_DEV_INSPECTOR || !inspectorActivatedByUrl) return;
    if (sessionInspector?.enabled) return;
    sessionApi.setSession((prev) => ({ inspector: { ...(prev.inspector || {}), enabled: true } }));
  }, [sessionApi, sessionInspector?.enabled, inspectorActivatedByUrl]);

  // Restore selection + temp edits from session (safe selector strategy only).
  useEffect(() => {
    if (!ENABLE_DEV_INSPECTOR || !inspectActive) return;
    if (restoredSelectionRef.current) return;
    restoredSelectionRef.current = true;

    const savedKey = sessionInspector?.selectedElement;
    const savedEdits = sessionInspector?.tempEdits || {};
    if (!savedKey) {
      setOverrides(savedEdits);
      return;
    }

    // Defer one tick to allow page content to render.
    window.setTimeout(() => {
      const el = resolveStableElementKey(savedKey);
      if (el && isInspectable(el)) {
        updateSelection(el, savedEdits, savedKey);
        return;
      }
      // If we can't safely restore selection, clear it but keep other session state.
      sessionApi?.setSession((prev) => ({ inspector: { ...(prev.inspector || {}), enabled: true, selectedElement: null } }));
      setOverrides(savedEdits);
    }, 0);
  }, [inspectActive, sessionInspector?.selectedElement, sessionInspector?.tempEdits, sessionApi, updateSelection]);

  const handleResetSelectedEdits = useCallback(() => {
    if (selectedElement?.isConnected) clearOverridesFromElement(selectedElement, overrides);
    setOverrides({});
    sessionApi?.setSession((prev) => ({ inspector: { ...(prev.inspector || {}), enabled: true, tempEdits: {} } }));
  }, [selectedElement, overrides, sessionApi]);

  // Persist temp edits (do not persist hover / DOM refs).
  useEffect(() => {
    if (!sessionApi) return;
    if (!ENABLE_DEV_INSPECTOR || !inspectActive) return;
    sessionApi.setSession((prev) => ({ inspector: { ...(prev.inspector || {}), enabled: true, tempEdits: overrides } }));
  }, [sessionApi, inspectActive, overrides]);

  useEffect(() => {
    if (!ENABLE_DEV_INSPECTOR || !inspectActive) return;

    const onMouseMove = (e) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const el = document.elementFromPoint(e.clientX, e.clientY);
        if (el && isInspectable(el)) {
          updateHoverRect(el);
        } else {
          setHoveredElement(null);
          setHoverRect(null);
        }
        rafRef.current = null;
      });
    };

    const onClick = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el && isInspectable(el)) {
        e.preventDefault();
        e.stopPropagation();
        restoredSelectionRef.current = true; // user has explicitly selected
        updateSelection(el, {}, deriveStableElementKey(el));
      }
    };

    const onScrollOrResize = () => {
      if (hoveredElement?.isConnected && hoveredElement.getBoundingClientRect) {
        setHoverRect(hoveredElement.getBoundingClientRect());
      }
      if (selectedElement?.isConnected && selectedElement.getBoundingClientRect) {
        setSelectedBoxModel(getBoxModelRects(selectedElement));
      }
    };

    document.addEventListener("mousemove", onMouseMove, { passive: true });
    document.addEventListener("click", onClick, true);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ENABLE_DEV_INSPECTOR, inspectActive, updateHoverRect, updateSelection, hoveredElement, selectedElement]);

  const handleClearSelection = useCallback(() => {
    if (selectedElement?.isConnected) clearOverridesFromElement(selectedElement, overrides);
    previousSelectedRef.current = null;
    setSelectedElement(null);
    setSelectedBoxModel(null);
    setLayoutInfo(null);
    setOverrides({});
    sessionApi?.setSession((prev) => ({ inspector: { ...(prev.inspector || {}), enabled: true, selectedElement: null, tempEdits: {} } }));
  }, [selectedElement, overrides, sessionApi]);

  const handleClose = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("inspect");
    setSearchParams(next, { replace: true });
    handleClearSelection();
    setHoveredElement(null);
    setHoverRect(null);
    setOverrides({});
    restoredSelectionRef.current = false;
    sessionApi?.setSession((prev) => ({ inspector: { ...(prev.inspector || {}), enabled: false, selectedElement: null, tempEdits: {} } }));
  }, [searchParams, setSearchParams, handleClearSelection, sessionApi]);

  useEffect(() => {
    if (!ENABLE_DEV_INSPECTOR || !inspectActive || !stack) return;
    const isSmart = inspectorMode === INSPECTOR_MODE_SMART;
    const panelTitle = isSmart ? "Smart Inspector (experimental)" : "Layout Inspector";
    stack.registerPanel("inspector", () => (
      <DevtoolsPanelShell title={panelTitle}>
        {isSmart ? (
          <SmartInspectorPanel
            layoutInfo={layoutInfo}
            introspection={smartIntrospection}
            showHoverOverlay={showHoverOverlay}
            onToggleHoverOverlay={setShowHoverOverlay}
            onClearSelection={handleClearSelection}
            onClose={handleClose}
          />
        ) : (
          <ClassicInspectorPanel
            layoutInfo={layoutInfo}
            showHoverOverlay={showHoverOverlay}
            onToggleHoverOverlay={setShowHoverOverlay}
            onClearSelection={handleClearSelection}
            onClose={handleClose}
            overrides={overrides}
            onOverridesChange={setOverrides}
            onResetSelectedEdits={handleResetSelectedEdits}
            mediaCompositionInfo={mediaCompositionInfo}
            onMediaCompositionPatch={handleMediaCompositionPatch}
            reviewCardHeightInfo={reviewCardHeightInfo}
            onReviewCardHeightPatch={handleReviewCardHeightPatch}
          />
        )}
      </DevtoolsPanelShell>
    ));
    return () => stack.unregisterPanel("inspector");
  }, [
    ENABLE_DEV_INSPECTOR,
    inspectActive,
    inspectorMode,
    stack,
    layoutInfo,
    showHoverOverlay,
    handleClearSelection,
    handleClose,
    overrides,
    handleResetSelectedEdits,
    mediaCompositionInfo,
    handleMediaCompositionPatch,
    reviewCardHeightInfo,
    handleReviewCardHeightPatch,
    smartIntrospection,
  ]);

  if (!ENABLE_DEV_INSPECTOR || !inspectActive) return null;

  return (
    <div className="layout-inspector-overlay" aria-hidden style={{ pointerEvents: "none" }}>
      {showHoverOverlay && hoverRect && (
        <div
          className="layout-inspector-hover-rect"
          style={{
            left: hoverRect.left,
            top: hoverRect.top,
            width: hoverRect.width,
            height: hoverRect.height,
          }}
        />
      )}
      {selectedBoxModel &&
        ["margin", "border", "padding", "content"].map((key) => (
          <div
            key={key}
            className={`layout-inspector-box layout-inspector-box--${key}`}
            style={{
              left: selectedBoxModel[key].left,
              top: selectedBoxModel[key].top,
              width: selectedBoxModel[key].width,
              height: selectedBoxModel[key].height,
            }}
          />
        ))}
    </div>
  );
}
